import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Checkbox } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import styled from 'styled-components';
import { formatReturnDate, OutOfStockItem } from '../OutOfStockUtils';
import { ItemTypeChip, MetaLabel } from '../OutOfStockShared';

type Props = {
  item: OutOfStockItem;
  checked: boolean;
  /** True while a restore is in flight, so the selection cannot drift under the request. */
  isSubmitting: boolean;
  onToggle: (itemId: number) => void;
  onOpen: (item: OutOfStockItem) => void;
};

/**
 * One row of the restaurant's out-of-stock list: a selection checkbox, and a button opening the edit
 * dialog. Only items with a return date can be edited or restored, so an open-ended item is inert.
 */
export const OutOfStockRow: React.FC<Props> = ({ item, checked, isSubmitting, onToggle, onOpen }) => {
  const { t } = useTranslation();
  const isEditable = Boolean(item.scheduledUntil);

  return (
    <Row>
      <Checkbox
        color="primary"
        checked={checked}
        onChange={() => onToggle(item.itemId)}
        disabled={!isEditable || isSubmitting}
        inputProps={{ 'aria-label': item.name ?? undefined }}
      />

      <ItemButton type="button" disabled={!isEditable || isSubmitting} onClick={() => onOpen(item)}>
        <ItemDetails>
          <NameGroup>
            <Name>{item.name}</Name>
            <ItemTypeChip type={item.type} />
          </NameGroup>

          <StateGroup>
            {item.scheduledUntil ? (
              <>
                {/* Phones have no column header to read the date against, so it labels itself. */}
                <StateLabel>{t('singleRestaurant.outOfStock.returns')}</StateLabel>
                <RowState>{formatReturnDate(item.scheduledUntil)}</RowState>
              </>
            ) : (
              <IndefiniteBadge>{t('Temporarily out of stock')}</IndefiniteBadge>
            )}
          </StateGroup>
        </ItemDetails>

        {/* Always occupies its slot so the date column stays aligned across rows. */}
        <ChevronSlot>{isEditable && <Chevron fontSize="small" />}</ChevronSlot>
      </ItemButton>
    </Row>
  );
};

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};

  &:last-child {
    border-bottom: none;
  }
`;

// The clickable half of the row: the details column plus the chevron, which stays out of the column so
// it can sit on the right edge, centred across however many lines the details take. A real button
// rather than a clickable div, so the edit dialog can be opened from the keyboard too.
const ItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(1)};
  border: 0;
  border-radius: 8px;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => alpha(theme.palette.text.primary, 0.03)};
  }

  &:disabled {
    cursor: default;
    background: none;
  }
`;

// Two stacked lines on phones (name, then the labelled state); the two columns of the table from sm up.
const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
`;

const NameGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
`;

const Name = styled.span`
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  overflow-wrap: anywhere;
`;

const StateGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

// Stands in for the PALAUTUU column header, which phones do not show.
const StateLabel = styled(MetaLabel)`
  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: none;
  }
`;

const RowState = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

// Warning rather than error: an item that is out of stock indefinitely is a state to notice, not a
// failure. Replaces the previous plain `color: red` text.
const IndefiniteBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.warning.main};
  background-color: ${({ theme }) => alpha(theme.palette.warning.main, 0.14)};
`;

const ChevronSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 20px;
`;

const Chevron = styled(ChevronRight)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export default OutOfStockRow;
